import type { RPCRouter } from "@granular/fabric3-server";
import type { inferRouterOutputs } from "@trpc/server";
import { useAtom } from "jotai";
import { intersection, keyBy } from "lodash-es";
import React, { useCallback, useMemo } from "react";
import { coreQueryClient } from "../services/trpc";
import { statefulFiltersAtom } from "../state/filters";
import { useOperatingAgency } from "./operatingAgency";
import { useOperatingContext } from "./operatingContext";

type RouterOutput = inferRouterOutputs<RPCRouter>;
export type OperationsResponse = RouterOutput["bizContext"]["getOperations"];
export type SingleOperation =
  RouterOutput["bizContext"]["getOperations"][number];

const selectOperations = (operations: OperationsResponse) =>
  keyBy(operations, "id");

/**
 * This hook is used to get the Operations data from the API based on the current selected Context/Agency.
 * @param customAppName - Optional param to assign a specific key under which to store the app's state
 * @returns
 * - allOperations: All the operations data from the API.
 * - selectedOperationIds: The operation selected by the user.
 * - setSelectedOperationIds: Function to set the operation selected by the user.
 * - isLoading: boolean to indicate if the data is still loading.
 * - isError: boolean to indicate if there is an error while fetching the data.
 **/
export function useBusinessOperations(options?: { enabled?: boolean }) {
  const hookEnabled = options?.enabled ?? true;

  // getting the selected context from the context hook
  const { contextSelected } = useOperatingContext();
  const { selectedAgency, isLoading: isLoadingAgency } = useOperatingAgency();

  const contextIdForOperations =
    (contextSelected?.type === "District" && // MCS Districts house Operations
      contextSelected?.brand !== "pioneer") ||
    contextSelected?.type === "Agency" ||
    contextSelected?.type === "Operation"
      ? contextSelected?.contextId
      : selectedAgency?.contextId;

  const {
    data: allOperationsById,
    isPending: isLoadingOperations,
    isError,
  } = coreQueryClient.bizContext.getOperations.useQuery(
    { contextId: contextIdForOperations ?? "" },
    {
      enabled: hookEnabled && !!contextIdForOperations, // only fetch when context is selected
      // 2 minutes: short enough to pick up changes if a user rapidly switches context
      // within a session, long enough to prevent redundant refetches across the many
      // components that call this hook on the map page. Context switches produce a new
      // query key (contextId changes) so React Query refetches naturally regardless.
      staleTime: 1000 * 60 * 2,
      select: selectOperations,
    },
  );

  // This is the state atom that our PCB filter state lives in
  const [filters, setFilters] = useAtom(statefulFiltersAtom);

  /**
   * Check if the array of operation ids passed as parameter are valid operations for the selected context.
   * To be valid, the operation must exists in the allOperations array. Invalid operations will be removed.
   * @returns A sorted array with the valid operations.
   */
  const filterValidOperationIds = React.useCallback(
    (operationIds: string[] | undefined) => {
      // The selected operation ids should exists in the array of all operations ids to be valid
      return intersection(
        operationIds,
        Object.keys(allOperationsById ?? {}),
      ).toSorted();
    },
    [allOperationsById],
  );

  const selectedOperationIds = useMemo(() => {
    return filterValidOperationIds(filters.operations) ?? [];
  }, [filters.operations, filterValidOperationIds]);

  /**
   * Set selected operations in the current filter state
   */
  const setSelectedOperationIds = useCallback(
    (newOps?: string[]) => {
      setFilters((prev) => ({ ...prev, operations: newOps }));
      return newOps;
    },
    [setFilters],
  );

  // Only mark as loading if we have a context and we're waiting for results
  const isLoadingOperationsAndWillLoad =
    contextIdForOperations === undefined ? false : isLoadingOperations;

  return {
    currentAgencyId: selectedAgency?.contextId,
    allOperations: Object.values(allOperationsById ?? {}),
    allOperationsById,
    selectedOperationIds,
    setSelectedOperationIds,
    isLoading: isLoadingOperationsAndWillLoad || isLoadingAgency,
    isError,
    filterValidOperationIds,
  };
}
